/**
 * "Apri sul sito": the public address of a site, a page or a content.
 *
 * The rule is the publisher's own (`urlForContent` in
 * packages/site-kit/src/page.ts): a document that carries its own `path`
 * wins over the type's `urlPattern`, because a migrated page answers at the
 * address it had, not at one a pattern would produce. It is repeated here
 * rather than imported because the interface depends on contracts only, and
 * with one deliberate difference: when the pattern names a field this screen
 * does not have (a list row carries no `data`), the answer is `null` — no
 * link — instead of an address with a hole in it.
 */

export function contentPath(
  urlPattern: string | null | undefined,
  data: Record<string, unknown> | null | undefined,
  slug: string,
): string | null {
  const own = data?.['path']
  if (typeof own === 'string' && own.startsWith('/')) return own
  if (!urlPattern) return null
  let missing = false
  const path = urlPattern.replace(/\{(\w+)\}/g, (_match, field: string) => {
    if (field === 'slug') return slug
    const value = data?.[field]
    if (typeof value === 'string' && value) return value
    missing = true
    return ''
  })
  if (missing) return null
  return path.startsWith('/') ? path : `/${path}`
}

/** `https://<primary domain><path>`, or `null` when there is no domain to point at. */
export function liveUrl(domain: string | null | undefined, path: string | null = '/'): string | null {
  if (!domain || path === null) return null
  const host = domain.trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '')
  if (!host || /[\s/]/.test(host)) return null
  return `https://${host}${path.startsWith('/') ? path : `/${path}`}`
}

/** The domain as a person reads it: no scheme, no trailing slash. */
export function displayDomain(domain: string | null | undefined): string {
  return (domain ?? '').replace(/^https?:\/\//i, '').replace(/\/+$/, '')
}
